import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Loader2 } from 'lucide-react';
import { LanguageToggle } from './LanguageToggle';
import { ThemeToggle } from './ThemeToggle';
import { useRole } from '@/contexts/RoleContext';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { clearLocalStoragePreservingConsent } from '@/lib/cookieConsent';
import { CookieConsentSettingsButton } from '@/components/cookie-consent/CookieConsentRoot';

export const TopBar = () => {
  const { currentRole } = useRole();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const failsafeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // CRITICAL: Listen for SIGNED_OUT event to clear failsafe timeout
  // This ensures that if onAuthStateChange in App.tsx successfully handles SIGNED_OUT,
  // we don't need to execute the failsafe reload
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        console.log('[TopBar] SIGNED_OUT detected - czyszczenie failsafe timeout');
        // Clear failsafe timeout since logout was successful
        if (failsafeTimeoutRef.current) {
          clearTimeout(failsafeTimeoutRef.current);
          failsafeTimeoutRef.current = null;
        }
        // Reset logging out state
        setIsLoggingOut(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      // Cleanup failsafe timeout on unmount
      if (failsafeTimeoutRef.current) {
        clearTimeout(failsafeTimeoutRef.current);
        failsafeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleLogout = async () => {
    // Blokuj wielokrotne kliknięcia
    if (isLoggingOut) {
      console.log('[TopBar] handleLogout: już w trakcie wylogowania, pomijam');
      return;
    }

    setIsLoggingOut(true);
    console.log('[TopBar] handleLogout: start, timestamp=', Date.now());
    
    // CRITICAL: Wyczyść poprzedni failsafe timeout jeśli istnieje
    if (failsafeTimeoutRef.current) {
      clearTimeout(failsafeTimeoutRef.current);
      failsafeTimeoutRef.current = null;
    }
    
    try {
      console.log('[TopBar] handleLogout: wywołuję signOut...');
      
      // CRITICAL: Wywołaj signOut i poczekaj na zakończenie
      // Nie używaj Promise.race z timeoutem - pozwól signOut zakończyć się naturalnie
      await supabase.auth.signOut();
      
      console.log('[TopBar] handleLogout: signOut zakończony');
      
      // CRITICAL: Wyczyść localStorage PO zakończeniu signOut (w tym active_session_*),
      // aby nowy użytkownik nie przejął sesji pracy poprzedniego
      console.log('[TopBar] handleLogout: czyszczenie localStorage');
      clearLocalStoragePreservingConsent();
      
      // CRITICAL: NIE wykonuj natychmiastowego window.location.href
      // Pozwól onAuthStateChange w App.tsx naturalnie wykryć zdarzenie SIGNED_OUT
      // i zresetować stany session oraz userRole, co spowoduje renderowanie LandingPage
      
      // CRITICAL: Failsafe timeout - jeśli po 2 sekundach aplikacja nadal nie przełączyła
      // widoku na Landing Page, wykonaj twardy reset window.location.reload()
      failsafeTimeoutRef.current = setTimeout(() => {
        console.warn('[TopBar] handleLogout: Failsafe timeout - aplikacja nie przełączyła widoku po 2s, wymuszam reload');
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.log('[TopBar] handleLogout: CATCH error=', error);
      console.error('[TopBar] Nieoczekiwany błąd podczas wylogowania:', error);
      
      // CRITICAL: Nawet przy błędzie, wyczyść localStorage i ustaw failsafe
      clearLocalStoragePreservingConsent();
      
      // Failsafe timeout również przy błędzie
      failsafeTimeoutRef.current = setTimeout(() => {
        console.warn('[TopBar] handleLogout: Failsafe timeout (error case) - wymuszam reload');
        window.location.reload();
      }, 2000);
    }
    
    // CRITICAL: NIE resetuj isLoggingOut tutaj - pozwól onAuthStateChange obsłużyć SIGNED_OUT
    // Stan zostanie zresetowany gdy aplikacja przełączy się na LandingPage
    // lub gdy failsafe timeout wykona reload
  };

  // Determine the link destination based on role
  const getLogoLink = () => {
    if (currentRole === 'owner' || currentRole === 'coordinator') {
      return '/dashboard';
    } else if (currentRole === 'cleaner') {
      return '/';
    }
    return '/';
  };

  const logoClassName = "truncate text-base font-bold cursor-pointer hover:opacity-80 transition-opacity sm:text-xl whitespace-nowrap";

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-white dark:bg-[#0C1020] shadow-sm">
      <div className="container mx-auto flex h-16 min-w-0 items-center justify-between gap-2 px-3 sm:px-4">
        {/* Branding - Left Side */}
        <div className="flex min-w-0 items-center overflow-hidden">
          {/* Cleaner dashboard uses BrowserRouter (see App.tsx); Link avoids full page reload */}
          {currentRole === 'cleaner' ? (
            <Link 
              to="/"
              className={logoClassName}
            >
              <span className="text-gray-900 dark:text-white">DOMIO</span>
              <span className="text-[#2C7BE5] dark:text-[#60A5FA] ml-1">Cleaning</span>
            </Link>
          ) : (
            /* For owner/coordinator, use Link component (BrowserRouter is mounted) */
            <Link 
              to={getLogoLink()}
              className={logoClassName}
            >
              <span className="text-gray-900 dark:text-white">DOMIO</span>
              <span className="text-[#2C7BE5] dark:text-[#60A5FA] ml-1">Cleaning</span>
            </Link>
          )}
        </div>

        {/* Controls - Right Side */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-3">
          {/* Language Switcher */}
          <LanguageToggle />

          {/* Theme Toggle */}
          <CookieConsentSettingsButton variant="icon" />
          <ThemeToggle />

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={cn(
              "h-9 w-9 flex items-center justify-center",
              "text-muted-foreground hover:text-foreground",
              "rounded-lg border border-border/50",
              "hover:border-border hover:bg-accent",
              "transition-all duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              isLoggingOut && "opacity-50 cursor-not-allowed"
            )}
            aria-label="Wyloguj się"
          >
            {isLoggingOut ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </nav>
  );
};
